import { useEffect, useState, useMemo, forwardRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';

/**
 * Input with optional regex validation against a server-supplied pattern.
 * - `patterns` is the dict from `meta.validation_patterns`.
 * - `patternKey` selects which one (pan/gst/cin/ifsc/aadhar/din).
 * - Empty value is always treated as valid.
 * - Normalizes  / strips spaces+dashes on blur.
 */
const ValidatedInput = forwardRef(({
  label, patterns, patternKey, value, onChange, required, testid, placeholder, hint, ...rest
}, ref) => {
  const [touched, setTouched] = useState(false);
  const spec = patterns?.[patternKey];

  const error = useMemo(() => {
    if (!touched || !value || !spec) return '';
    const re = new RegExp(spec.pattern);
    const normalized = String(value).replace(/[\s-]/g, '').toUpperCase();
    return re.test(normalized) ? '' : spec.message;
  }, [touched, value, spec]);

  // Live-sanitize for digits-only fields
  const handleChange = (e) => {
    let v = e.target.value;
    if (patternKey === 'aadhar' || patternKey === 'din') {
      v = v.replace(/\D/g, '');
    }
    onChange(v);
  };

  const handleBlur = () => {
    setTouched(true);
    // Normalize alphanumeric IDs to  + strip whitespace/dashes
    if (value && spec && patternKey !== 'aadhar' && patternKey !== 'din') {
      const norm = String(value).replace(/[\s-]/g, '').toUpperCase();
      if (norm !== value) onChange(norm);
    }
  };

  return (
    <div className="space-y-1.5">
      {label && (
        <Label className="text-xs font-medium text-gray-600  tracking-wider">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      <Input
        ref={ref}
        data-testid={testid}
        value={value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder || spec?.message?.split(':')[0] || ''}
        className={error ? 'border-red-400 focus-visible:ring-red-300' : ''}
        {...rest}
      />
      {error ? (
        <div data-testid={`${testid}-error`} className="flex items-center gap-1 text-xs text-red-600">
          <AlertCircle className="w-3 h-3" /> {error}
        </div>
      ) : hint ? (
        <div className="text-xs text-gray-400">{hint}</div>
      ) : null}
    </div>
  );
});

ValidatedInput.displayName = 'ValidatedInput';
export default ValidatedInput;
